'use client'

import { useEffect, useState } from 'react'

import { ArrowUp, MapPin } from 'lucide-react'

import { cn } from '@/lib/utils'
import { stadiums } from '@/content/world-cup-2026/data'

/** Short option label, e.g. "New York New Jersey Stadium" → "New York New Jersey". */
const shortLabel = (name: string) => name.replace(/ Stadium$/, '')

/**
 * Sticky "jump to a stadium" bar with scroll-spy. Reveals once the hero scrolls
 * out of view and tracks which stadium card is currently in view. Client-only —
 * it reads scroll position and element geometry, so it renders nothing useful
 * server-side and is purely an enhancement over the in-page TOC.
 */
export const WcJumpBar = () => {
    const [visible, setVisible] = useState(false)
    const [current, setCurrent] = useState(stadiums[0]?.id ?? '')

    useEffect(() => {
        const onScroll = () => {
            const hero = document.getElementById('top')
            const heroBottom = hero ? hero.offsetTop + hero.offsetHeight : 400
            setVisible(window.scrollY > heroBottom - 90)

            let active: string | null = null
            for (const s of stadiums) {
                const el = document.getElementById(s.id)
                if (!el) {
                    continue
                }
                if (el.getBoundingClientRect().top <= 130) {
                    active = s.id
                } else {
                    break
                }
            }
            if (active) {
                setCurrent(active)
            }
        }

        window.addEventListener('scroll', onScroll, { passive: true })
        onScroll()
        return () => window.removeEventListener('scroll', onScroll)
    }, [])

    const jumpTo = (id: string) => {
        document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' })
        setCurrent(id)
    }

    return (
        <div
            aria-hidden={!visible}
            className={cn(
                'fixed top-[50px] right-0 left-0 z-20 border-b border-[var(--wc-border)] bg-white shadow-[var(--wc-shadow-1)] transition-all duration-200 lg:top-0',
                visible
                    ? 'translate-y-0 opacity-100'
                    : 'pointer-events-none -translate-y-[130%] opacity-0',
            )}
        >
            <div className="mx-auto flex max-w-[1400px] items-center gap-3 px-6 py-2">
                <span className="inline-flex items-center gap-[7px] text-[13px] font-semibold whitespace-nowrap text-[var(--wc-fg-2)]">
                    <MapPin className="h-4 w-4 text-[var(--wc-blu-700)]" aria-hidden="true" />
                    <span className="max-[600px]:hidden">Jump to a stadium</span>
                </span>
                <select
                    aria-label="Jump to a stadium"
                    value={current}
                    onChange={(e) => jumpTo(e.target.value)}
                    className="h-[38px] max-w-[280px] cursor-pointer rounded-[3px] border border-[var(--wc-smoke-500)] bg-white px-3 text-sm font-semibold text-[var(--wc-fg)] outline-none focus:border-[var(--wc-blu-700)] focus:shadow-[0_0_0_2px_var(--wc-blu-300)] max-[600px]:max-w-none max-[600px]:flex-1"
                >
                    {stadiums.map((s) => (
                        <option key={s.id} value={s.id}>
                            {shortLabel(s.name)}
                        </option>
                    ))}
                </select>
                <div className="flex-1" />
                <a
                    href="#top"
                    className="inline-flex items-center gap-[6px] text-[13px] font-bold whitespace-nowrap text-[var(--wc-blu-700)]"
                >
                    <ArrowUp className="h-[15px] w-[15px]" aria-hidden="true" />
                    <span className="max-[600px]:hidden">Back to top</span>
                </a>
            </div>
        </div>
    )
}
